"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useExplorer } from "@/hooks/use-explorer"
import { getCachedSchema } from "@/lib/ats-schema-cache"
import { buildEntityGraph, neighboursOf, type EntityGraph } from "@/lib/entity-graph"
import type { AtsEntityTypeInfo, AtsFieldDescriptor } from "@/types/ats-explorer-api"

/** How many entities to resolve eagerly when the map opens (the first group is the core one) */
const EAGER_RESOLVE_LIMIT = 14

export interface UseEntityGraphResult {
  graph: EntityGraph
  /** Entities whose schema is being fetched right now */
  resolving: Set<string>
  /** Fetch an entity's schema so its associations appear */
  resolve: (entityName: string) => void
}

/**
 * The tenant's entity graph, filled in lazily: the core entities and the selected one
 * resolve when the map opens, the selected entity's neighbours resolve next, and any
 * node the user clicks resolves on demand. Schemas come from the shared session cache,
 * so nothing the explorer already fetched is fetched again.
 */
export function useEntityGraph(atsSystem: string | null, entities: AtsEntityTypeInfo[], selectedEntity: string | null): UseEntityGraphResult {
  const { getSchema } = useExplorer()
  const [schemas, setSchemas] = useState<Map<string, AtsFieldDescriptor[]>>(new Map())
  const [resolving, setResolving] = useState<Set<string>>(new Set())
  const requested = useRef<Set<string>>(new Set())

  const resolve = useCallback(
    (entityName: string) => {
      if (!atsSystem || requested.current.has(entityName)) return
      requested.current.add(entityName)
      setResolving((current) => new Set(current).add(entityName))
      getCachedSchema(atsSystem, entityName, () => getSchema(atsSystem, entityName, 3))
        .then((schema) => setSchemas((current) => new Map(current).set(entityName, schema.fields)))
        .catch(() => {
          // Leave the node unresolved; a later click retries
          requested.current.delete(entityName)
        })
        .finally(() =>
          setResolving((current) => {
            const next = new Set(current)
            next.delete(entityName)
            return next
          })
        )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [atsSystem]
  )

  // Core entities + the selection resolve first
  useEffect(() => {
    if (!atsSystem || entities.length === 0) return
    const firstGroup = entities[0]?.group
    entities
      .filter((e) => e.group === firstGroup)
      .slice(0, EAGER_RESOLVE_LIMIT)
      .forEach((e) => resolve(e.name))
    if (selectedEntity) resolve(selectedEntity)
  }, [atsSystem, entities, selectedEntity, resolve])

  const graph = useMemo(() => buildEntityGraph(entities, schemas), [entities, schemas])

  // Then the selection's neighbours, so the lit path has somewhere to go
  useEffect(() => {
    if (!selectedEntity) return
    neighboursOf(graph, selectedEntity).forEach((n) => resolve(n.node.id))
  }, [graph, selectedEntity, resolve])

  return { graph, resolving, resolve }
}
